import { ValueObject } from '../shared/ValueObject';
import { ValidationError } from '../errors/ValidationError';

/**
 * 現場ID値オブジェクト
 *
 * データベースのINT AUTO_INCREMENT型に対応
 * 後方互換性を保証
 *
 * 不変条件:
 * - 正の整数であること
 * - データベースのINT型の範囲内であること（1 ~ 2147483647）
 */
export class SiteId extends ValueObject<number> {
  // MySQL INT型の最大値
  private static readonly MAX_INT_VALUE = 2147483647;
  private static readonly MIN_ID_VALUE = 1;

  private constructor(value: number) {
    super(value);
  }

  /**
   * SiteIdを生成
   * @throws {ValidationError} 不正な値の場合
   */
  public static create(value: number): SiteId {
    SiteId.validate(value);
    return new SiteId(value);
  }

  /**
   * プリミティブ型から生成（後方互換性のため）
   */
  public static fromPrimitive(value: number): SiteId {
    return SiteId.create(value);
  }

  /**
   * プリミティブ型に変換（後方互換性のため）
   */
  public toPrimitive(): number {
    return this.value;
  }

  private static validate(value: number): void {
    if (value === null || value === undefined) {
      throw new ValidationError('現場IDは必須です');
    }

    if (!Number.isInteger(value)) {
      throw new ValidationError('現場IDは整数である必要があります');
    }

    if (value < SiteId.MIN_ID_VALUE) {
      throw new ValidationError(
        `現場IDは${SiteId.MIN_ID_VALUE}以上である必要があります`
      );
    }

    if (value > SiteId.MAX_INT_VALUE) {
      throw new ValidationError(
        `現場IDは${SiteId.MAX_INT_VALUE}以下である必要があります`
      );
    }
  }

  /**
   * 同一性の確認
   */
  public isSameAs(other: SiteId | null | undefined): boolean {
    return this.equals(other);
  }

  public toString(): string {
    return this.value.toString();
  }

  public toJSON(): number {
    return this.value;
  }

  /**
   * データベース保存用の値を取得
   */
  public toDatabaseValue(): number {
    return this.value;
  }
}